import moment from "moment";
import { TDocumentDefinitions } from "pdfmake/interfaces";

import { HeaderLogoWithAddress } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

import { formatDate, formatDatetime, formatTime } from "react-lib/utils/dateUtils";

type PhysicalTherapyScheduleProps = {
  appointments: AppointmentType[];
  language?: "EN" | "TH";
  patient: {
    age: string;
    birthdate: string;
    hn: string;
    name: string;
    sex: string;
  };
  printed_by?: string;
};

type AppointmentType = {
  estimated_at_iso: string;
  order_note: string;
  physiotherapist_fullname: string;
  summary_detail: string;
};

const FONT_SIZE = 11;
const FORM_NAME = "FormPhysicalTherapySchedule";

// Language translations
const translations = {
  EN: {
    age: "Age",
    birthdate: "Birthdate",
    headers: {
      advice: "Additional advice",
      date: "Date",
      no: "No.",
      program: "Physical Therapy Program",
      therapist: "Attending Physical Therapist",
      time: "Time",
    },
    hn: "HN",
    patientName: "Patient Name",
    printDateTime: "Print Date-time",
    printedBy: "Printed by",
    sex: "Sex",
    title: "Physical Therapy Appointment Schedule",
  },
  TH: {
    age: "อายุ",
    birthdate: "วันเกิด",
    headers: {
      advice: "คำแนะนำสำคัญผู้ป่วย",
      date: "วันที่",
      no: "ครั้งที่",
      program: "โปรแกรมการรักษา",
      therapist: "นักกายภาพบำบัดผู้ดูแล",
      time: "เวลา",
    },
    hn: "HN",
    patientName: "ชื่อ - นามสกุล",
    printDateTime: "วัน-เวลาที่พิมพ์",
    printedBy: "ผู้พิมพ์โดย",
    sex: "เพศ",
    title: "ตารางการนัดหมายกายภาพบำบัด",
  },
};

const parseHtmlToArray = (html: string) => {
  const regex = /(\d+)([a-z]+)\s+(.+)/;

  return html
    .split("<br/>")
    .filter(Boolean)
    .map((item) => {
      const text = item.replaceAll(/<[^>]*>/g, "");
      const match = regex.exec(text);
      const [, number, suffix, name] = match ?? [];

      return { name, number: Number(number), suffix };
    });
};

const FormPhysicalTherapySchedule = async (
  props: PhysicalTherapyScheduleProps
): Promise<TDocumentDefinitions> => {
  console.log("FormPhysicalTherapySchedule props:", props);

  const language = props.language || "EN";
  const t = translations[language];

  const headerForm = await HeaderLogoWithAddress({
    font: "KanitLM",
    form: FORM_NAME,
    headerMargins: [15, 15, 15, 0],
    logoHeight: 50,
    pageMargins: [15, 150, 15, 55],
    styles: {
      fieldHeader: {
        alignment: "center",
        bold: true,
        fontSize: 16,
        marginBottom: 5,
        marginTop: 5,
      },
      fieldKey: {
        bold: true,
        fontSize: FONT_SIZE,
      },
      fieldValue: {
        fontSize: FONT_SIZE,
      },
      tableData: {
        alignment: "center",
        fontSize: FONT_SIZE - 0.5,
      },
      tableHeader: {
        alignment: "center",
        bold: true,
        fontSize: FONT_SIZE,
      },
    },
    titleContent: [
      {
        style: "fieldHeader",
        text: t.title,
      },
      {
        columns: [
          {
            text: [
              { style: "fieldKey", text: `${t.patientName} : ` },
              { style: "fieldValue", text: props.patient.name },
            ],
            width: "35%",
          },
          {
            text: [
              { style: "fieldKey", text: `${t.hn} : ` },
              { style: "fieldValue", text: props.patient.hn },
            ],
            width: "15%",
          },
          {
            text: [
              { style: "fieldKey", text: `${t.sex} : ` },
              { style: "fieldValue", text: props.patient.sex },
            ],
            width: "15%",
          },
          {
            text: [
              { style: "fieldKey", text: `${t.birthdate} : ` },
              { style: "fieldValue", text: props.patient.birthdate },
            ],
            width: "20%",
          },
          {
            text: [
              { style: "fieldKey", text: `${t.age} : ` },
              { style: "fieldValue", text: props.patient.age },
            ],
            width: "*",
          },
        ],
        margin: [0, 3, 0, 3],
      },
    ],
  });

  const { font, images, styles } = headerForm;

  return {
    defaultStyle: {
      font,
      fontSize: FONT_SIZE,
      lineHeight: 1.2,
    },
    pageOrientation: "landscape",
    pageSize: "A4",
    ...styles,
    ...headerForm,
    images: {
      ...images,
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths: ["5%", "11%", "9%", "30%", "20%", "*"],
          body: [
            [
              { style: "tableHeader", text: t.headers.no },
              { style: "tableHeader", text: t.headers.date },
              { style: "tableHeader", text: t.headers.time },
              { style: "tableHeader", text: t.headers.program },
              { style: "tableHeader", text: t.headers.therapist },
              { style: "tableHeader", text: t.headers.advice },
            ],
            ...props.appointments.map((appointment, index) => {
              const formattedTime = formatTime(appointment.estimated_at_iso);
              const doctorNote = appointment.order_note.match(/^.*[^\n\r]/gm) ?? [];

              return [
                {
                  style: "tableData",
                  text: index + 1,
                },
                {
                  style: "tableData",
                  text: formatDate(moment(appointment.estimated_at_iso)),
                },
                {
                  style: "tableData",
                  text: language === "TH" ? formattedTime.time_th : formattedTime.time_en,
                },
                {
                  alignment: "left",
                  margin: [8, 4, 4, 4],
                  style: "tableData",
                  ul: parseHtmlToArray(appointment.summary_detail).map((program) => ({
                    style: "fieldValue",
                    text: [
                      { text: program.number },
                      {
                        sup: true,
                        text: program.suffix,
                      },
                      { text: ` ${program.name}` },
                    ],
                  })),
                },
                {
                  alignment: "left",
                  margin: [8, 4, 4, 4],
                  style: "tableData",
                  text: appointment.physiotherapist_fullname,
                },
                {
                  alignment: "left",
                  fontSize: FONT_SIZE - 1,
                  margin: [8, 4, 4, 4],
                  style: "tableData",
                  ul: doctorNote.map((advice: string) => ({
                    style: "fieldValue",
                    text: advice.replace(/^-/, ""),
                  })),
                },
              ];
            }),
          ],
        },
        layout: {
          hLineColor: () => "#000000",
          hLineWidth: () => 0.5,
          paddingBottom: () => 4,
          paddingLeft: () => 2,
          paddingRight: () => 2,
          paddingTop: () => 4,
          vLineColor: () => "#000000",
          vLineWidth: () => 0.5,
        },
      },
    ],
    footer: () => ({
      alignment: "right",
      margin: [15, 10, 15, 0],
      stack: [
        {
          fontSize: FONT_SIZE - 1,
          text: `${t.printDateTime} : ${formatDatetime(moment())}`,
        },
        {
          fontSize: FONT_SIZE - 1,
          marginTop: 0,
          text: `${t.printedBy} : ${props.printed_by || ""}`,
        },
      ],
    }),
  };
};

export default FormPhysicalTherapySchedule;
